import { useState, useEffect } from 'react';
import { useParticipantsStore } from '../../stores/participantsStore';
import { useRoomStore } from '../../stores/roomStore';
import { useChatStore } from '../../stores/chatStore';
import VideoGrid from './VideoGrid';
import ControlBar from './ControlBar';
import ParticipantsList from './ParticipantsList';
import Chat from './Chat';
import WebRTCMonitor from './WebRTCMonitor';
import ConnectionStatus from './ConnectionStatus';

const Conference = () => {
  const { participants } = useParticipantsStore();
  const { roomId } = useRoomStore();
  const { unreadCount } = useChatStore();
  const [duration, setDuration] = useState(0);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showMonitor, setShowMonitor] = useState(false);
  const [showMonitorButton, setShowMonitorButton] = useState(false);

  // Таймер продолжительности звонка
  useEffect(() => {
    const timer = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDuration = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-900 overflow-hidden">
      {/* Connection Status Indicator */}
      <ConnectionStatus />
      
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-white">Видеоконференция</h1>
            <p className="text-xs text-gray-400">
              {participants.size + 1} участник{participants.size + 1 !== 1 ? 'а' : ''}
              {roomId && (
                <span className="ml-2 text-gray-500">• ID: {roomId}</span>
              )}
            </p>
          </div>
          <div className="text-gray-400 text-sm">
            {formatDuration(duration)}
          </div>
        </div>
      </div>

      {/* Main Content Area - flex row on desktop, column on mobile */}
      <div className="flex-1 flex md:flex-row flex-col overflow-hidden relative">
        {/* Video Grid Container - shrinks when sidebar is open on desktop */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <VideoGrid />
          
          {/* Control Bar */}
          <ControlBar
            onToggleSidebar={() => {
              setShowParticipants(!showParticipants);
              if (showChat) setShowChat(false);
            }}
            onToggleChat={() => {
              setShowChat(!showChat);
              if (showParticipants) setShowParticipants(false);
            }}
            onToggleMonitor={() => setShowMonitor(!showMonitor)}
            showMonitorButton={showMonitorButton}
            unreadMessages={unreadCount}
          />
        </div>

        {/* Sidebar - Participants - in flow on desktop, overlay on mobile */}
        {showParticipants && (
          <div className="md:relative md:w-80 md:flex-shrink-0 absolute right-0 top-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 shadow-2xl z-10 animate-slide-in-right">
            <ParticipantsList onClose={() => setShowParticipants(false)} />
          </div>
        )}

        {/* Sidebar - Chat - in flow on desktop, overlay on mobile */}
        {showChat && (
          <div className="md:relative md:w-80 md:flex-shrink-0 absolute right-0 top-0 bottom-0 w-80 bg-gray-800 border-l border-gray-700 shadow-2xl z-10 animate-slide-in-right">
            <Chat 
              onClose={() => setShowChat(false)} 
              onShowMonitor={() => setShowMonitorButton(true)}
            />
          </div>
        )}
      </div>

      {/* WebRTC Monitor */}
      <WebRTCMonitor isOpen={showMonitor} onClose={() => setShowMonitor(false)} />
    </div>
  );
};

export default Conference;
